import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  ArticleCommentRow,
  ArticleDetail,
  Paginated,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// Subscribe / unsubscribe — keyed by slug, since that's how ArticleViewSet
// looks up its rows (the URL is /articles/<slug>/, not /<id>/).
// ---------------------------------------------------------------------------

export function useSubscribeArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await api.post<ArticleDetail>(`/articles/${slug}/subscribe/`);
      return data;
    },
    onSuccess: (_a, slug) => {
      qc.invalidateQueries({ queryKey: ["articles", "detail", slug] });
      qc.invalidateQueries({ queryKey: ["articles"] });
    },
  });
}

export function useUnsubscribeArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await api.post<ArticleDetail>(`/articles/${slug}/unsubscribe/`);
      return data;
    },
    onSuccess: (_a, slug) => {
      qc.invalidateQueries({ queryKey: ["articles", "detail", slug] });
      qc.invalidateQueries({ queryKey: ["articles"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Comments — backend keys by article *id*, so the hook takes both id (for
// the network filter) and slug (for invalidating the detail query cache).
// ---------------------------------------------------------------------------

export function useArticleComments(articleId: string | undefined) {
  return useQuery<Paginated<ArticleCommentRow>>({
    enabled: articleId != null && !Number.isNaN(articleId),
    queryKey: ["article-comments", articleId],
    queryFn: async () => {
      const { data } = await api.get<Paginated<ArticleCommentRow>>(
        `/article-comments/?article=${articleId}`,
      );
      return data;
    },
  });
}

export function useCreateArticleComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { article: string; slug: string; body: string; reply_to?: string }) => {
      const { data } = await api.post<ArticleCommentRow>("/article-comments/", {
        article: input.article, body: input.body, reply_to: input.reply_to,
      });
      return data;
    },
    onSuccess: (_c, vars) => {
      qc.invalidateQueries({ queryKey: ["article-comments", vars.article] });
      qc.invalidateQueries({ queryKey: ["articles", "detail", vars.slug] });
    },
  });
}

export function useUpdateArticleComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; article: string; body: string }) => {
      const { data } = await api.patch<ArticleCommentRow>(
        `/article-comments/${input.id}/`,
        { body: input.body },
      );
      return data;
    },
    onSuccess: (_c, vars) =>
      qc.invalidateQueries({ queryKey: ["article-comments", vars.article] }),
  });
}

export function useDeleteArticleComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; article: string; slug: string }) => {
      await api.delete(`/article-comments/${input.id}/`);
    },
    onSuccess: (_x, vars) => {
      qc.invalidateQueries({ queryKey: ["article-comments", vars.article] });
      qc.invalidateQueries({ queryKey: ["articles", "detail", vars.slug] });
    },
  });
}
